import { Popover as BasePopover } from '@base-ui/react/popover';
import type { ComponentProps, ReactNode } from 'react';
import { LAYER } from '../lib/layer.ts';
import { cx } from '../lib/styled.tsx';
import { popupArrow, popupSurface, popupTransition } from '../lib/surfaces.ts';

type PositionerProps = ComponentProps<typeof BasePopover.Positioner>;

export interface PopoverProps extends ComponentProps<typeof BasePopover.Root> {
  trigger?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  side?: PositionerProps['side'];
  align?: PositionerProps['align'];
  sideOffset?: PositionerProps['sideOffset'];
  /** Point a small tip at the trigger. Off by default — the anchor is usually clear. */
  arrow?: boolean;
  className?: string;
}

/**
 * A panel anchored to its trigger, for content that would crowd the page but
 * does not deserve the interruption of a {@link Dialog}: a filter form, a
 * date picker, a short explanation with a link in it.
 */
export function Popover({
  trigger,
  title,
  description,
  children,
  side = 'bottom',
  align = 'center',
  sideOffset = 8,
  arrow = false,
  className,
  ...rootProps
}: PopoverProps) {
  return (
    <BasePopover.Root {...rootProps}>
      {trigger ? <BasePopover.Trigger render={trigger as never} /> : null}
      <BasePopover.Portal>
        <BasePopover.Positioner
          align={align}
          className={LAYER.popup}
          side={side}
          sideOffset={sideOffset}
        >
          <BasePopover.Popup
            className={cx(
              popupSurface,
              popupTransition,
              'w-[min(20rem,calc(100vw-2rem))] p-4',
              className,
            )}
          >
            {arrow ? (
              <BasePopover.Arrow
                className={cx(
                  popupArrow,
                  'before:bg-surface-raised before:border before:border-border-subtle',
                )}
              />
            ) : null}
            {title ? (
              <BasePopover.Title className="font-display text-md font-bold tracking-tight">
                {title}
              </BasePopover.Title>
            ) : null}
            {description ? (
              <BasePopover.Description className="mt-1.5 text-sm text-ink-muted">
                {description}
              </BasePopover.Description>
            ) : null}
            <div className={title || description ? 'mt-3' : undefined}>{children}</div>
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}

export const PopoverClose = BasePopover.Close;
